import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck } from "@fortawesome/free-solid-svg-icons";
import "./Introduction.css";

const HERO_SLIDES = [
  {
    src: "/images/introimage1.jpg",
    alt: "Axiomos team planning a digital product",
    label: "Product strategy",
  },
  {
    src: "/images/introimage2.jpg",
    alt: "Modern web and mobile application interface",
    label: "Design and engineering",
  },
  {
    src: "/images/introimage4.jpg",
    alt: "Cloud technology supporting business growth",
    label: "Launch and scale",
  },
];

const CAPABILITIES = [
  "Web and mobile applications",
  "Cloud and data solutions",
  "MVP to enterprise delivery",
];

function Introduction() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return undefined;

    const timer = window.setInterval(() => {
      setCurrentSlide((previous) => (previous + 1) % HERO_SLIDES.length);
    }, 5500);

    return () => window.clearInterval(timer);
  }, [isPaused]);

  return (
    <section className="intro-hero" aria-labelledby="intro-title">
      <div className="intro-hero__decoration" aria-hidden="true" />

      <div className="intro-hero__container">
        <div className="intro-hero__content">
          <p className="intro-hero__eyebrow">
            Strategy <span aria-hidden="true" /> Design <span aria-hidden="true" />
            Engineering
          </p>

          <h1 id="intro-title" className="intro-hero__title">
            Turn your business idea into a digital product
            <span> built to scale.</span>
          </h1>

          <p className="intro-hero__description">
            Axiomos helps ambitious companies plan, design, and engineer secure
            digital products—from focused MVPs to cloud-powered enterprise
            platforms.
          </p>

          <div className="intro-hero__actions">
            <Link className="intro-hero__button intro-hero__button--primary" to="/contact">
              Get a free estimate
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Link>

            <Link className="intro-hero__button intro-hero__button--secondary" to="/projects">
              Explore our work
            </Link>
          </div>

          <ul className="intro-hero__capabilities" aria-label="Core capabilities">
            {CAPABILITIES.map((capability) => (
              <li key={capability}>
                <span className="intro-hero__check" aria-hidden="true">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                {capability}
              </li>
            ))}
          </ul>
        </div>

        <div
          className="intro-hero__visual"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocusCapture={() => setIsPaused(true)}
          onBlurCapture={() => setIsPaused(false)}
        >
          <div className="intro-hero__image-frame">
            {HERO_SLIDES.map((slide, index) => (
              <img
                key={slide.src}
                src={slide.src}
                alt={index === currentSlide ? slide.alt : ""}
                className={`intro-hero__image${
                  index === currentSlide ? " intro-hero__image--active" : ""
                }`}
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
                aria-hidden={index !== currentSlide}
              />
            ))}

            <div className="intro-hero__image-overlay" aria-hidden="true" />

            <div className="intro-hero__image-caption" aria-live="polite">
              <span>0{currentSlide + 1}</span>
              <p>{HERO_SLIDES[currentSlide].label}</p>
            </div>

            <div className="intro-hero__pagination" aria-label="Select hero image">
              {HERO_SLIDES.map((slide, index) => (
                <button
                  key={slide.src}
                  type="button"
                  className={index === currentSlide ? "is-active" : ""}
                  aria-label={`Show slide ${index + 1}: ${slide.label}`}
                  aria-current={index === currentSlide ? "true" : undefined}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          </div>

          <div className="intro-hero__floating-card">
            <span className="intro-hero__floating-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faCheck} />
            </span>
            <div>
              <strong>End-to-end delivery</strong>
              <small>From strategy to launch and scale</small>
            </div>
          </div>

          <div className="intro-hero__experience-card">
            <strong>Purpose-led</strong>
            <span>technology with measurable business impact</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Introduction;
